import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { OrgActions } from "@/components/OrgActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, UserPlus, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

export default function OrgDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setActingOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");

  const { data: org, refetch } = useQuery({
    queryKey: ["org", id],
    queryFn: async () => (await api.get(`/admin/organisations/${id}`)).data,
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/admin/invite", { email, role: "org_admin", org_id: id })).data,
    onSuccess: (data) => {
      toast.success(data.sent_status === "sent" ? "Invite emailed" : "Invite created (email not sent)");
      setOpen(false); setEmail("");
      setLink(data.link);
      refetch();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const actAs = () => { setActingOrg(id); navigate("/org/dashboard"); };

  if (!org) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div>
      <button onClick={() => navigate("/admin/organisations")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-orgs">
        <ArrowLeft className="h-4 w-4" /> Organisations
      </button>
      <PageHeader
        title={org.name}
        description={org.archived ? `Archived${org.legal_name ? " · " + org.legal_name : ""}` : org.legal_name}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={actAs} data-testid="act-as-btn"><ArrowRightLeft className="mr-2 h-4 w-4" /> Act as organisation</Button>
            <OrgActions org={org} onChanged={refetch} onDeleted={() => navigate("/admin/organisations")} />
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-5"><p className="text-xs text-slate-400">Employees</p><p className="mt-1 text-2xl font-bold">{org.employee_count}</p></Card>
        <Card className="p-5"><p className="text-xs text-slate-400">Org admins</p><p className="mt-1 text-2xl font-bold">{org.admin_count}</p></Card>
        <Card className="p-5"><p className="text-xs text-slate-400">State</p><p className="mt-1 text-2xl font-bold">{org.state_code || "-"}</p></Card>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Organisation admins</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="invite-org-admin-btn"><UserPlus className="mr-2 h-4 w-4" /> Invite admin</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite organisation admin</DialogTitle></DialogHeader>
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="org-admin-email" /></div>
            <DialogFooter>
              <Button disabled={!email || invite.isPending} onClick={() => invite.mutate()} data-testid="org-admin-invite-submit">
                {invite.isPending ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mt-3 divide-y divide-slate-100">
        {org.admins?.length ? org.admins.map((a) => (
          <div key={a.membership_id} className="flex items-center justify-between px-5 py-3" data-testid={`admin-row-${a.email}`}>
            <div><p className="text-sm font-medium text-slate-800">{a.name}</p><p className="text-xs text-slate-400">{a.email}</p></div>
          </div>
        )) : <p className="px-5 py-6 text-center text-sm text-slate-400">No admins yet. Invite one above.</p>}
      </Card>

      <LinkDialog open={!!link} onClose={() => setLink("")} title="Invitation link"
        note="Share this link with the invitee. It works even if the email didn't arrive." link={link} />
    </div>
  );
}
